import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { StoreBadges } from '@/components/StoreBadges';
import { DownloadCloud, Smartphone, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const SITE_URL =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://thirrsty.com';

/**
 * DownloadAppDialog
 *
 * A universal "Get the app" modal for the marketing surface of Thirrsty.com.
 * Shows:
 *   1. A prominent "Add to Home Screen" button when the browser supports
 *      the PWA install prompt (Android Chrome, Edge, most Chromium browsers).
 *      Falls back to per-platform instructions on iOS Safari.
 *   2. Official App Store + Google Play badges (from StoreBadges).
 *      Automatically shows "Coming Soon" until you set STORE_URLS.
 *   3. A QR code encoding thirrsty.com so a desktop user can scan with
 *      their phone camera and open the site directly there.
 *
 * Controlled via `open` + `onOpenChange` (same pattern as other Radix Dialogs).
 */
export function DownloadAppDialog({ open, onOpenChange }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [copied, setCopied] = useState(false);

  // Detect browser install capability + iOS + already-installed state
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent || '';
    const iosLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iosLike);

    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleNativeInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        toast.success('Thirrsty is being added to your home screen');
        onOpenChange?.(false);
      }
    } catch (err) {
      toast.error('Install prompt failed — you can also use your browser menu.');
    } finally {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, onOpenChange]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success('Link copied — paste on your phone to install');
    } catch {
      toast.error('Could not copy — long-press the link to copy manually');
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl border-white/10 bg-[#0B0B10]/95 backdrop-blur-2xl text-white"
        data-testid="download-app-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-semibold"
            style={{ fontFamily: 'Space Grotesk' }}
            data-testid="download-app-title"
          >
            Get Thirrsty on your phone
          </DialogTitle>
          <DialogDescription className="text-white/70" data-testid="download-app-description">
            Install the app on your home screen for the fastest access to your QR
            code and the nightlife timeline.
          </DialogDescription>
        </DialogHeader>

        {/* Already installed — show a nice confirmation */}
        {isStandalone && (
          <div
            className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-3 text-sm text-emerald-100"
            data-testid="download-app-already-installed"
          >
            You already have Thirrsty installed. Open it from your home screen.
          </div>
        )}

        {/* Native install prompt (Android Chromium / desktop Chromium) */}
        {!isStandalone && deferredPrompt && (
          <Button
            onClick={handleNativeInstall}
            data-testid="download-app-native-install-button"
            className="h-12 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
          >
            <DownloadCloud className="mr-2 h-4 w-4" />
            Install Thirrsty on this device
          </Button>
        )}

        {/* iOS Safari install instructions */}
        {!isStandalone && isIOS && (
          <div
            className="rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm text-white/80"
            data-testid="download-app-ios-instructions"
          >
            <p className="font-semibold text-white">Install on iPhone / iPad:</p>
            <ol className="mt-1 list-decimal space-y-0.5 pl-5">
              <li>Tap the Share icon in Safari</li>
              <li>Scroll down and tap <span className="text-[#F6B24A] font-medium">Add to Home Screen</span></li>
              <li>Tap Add — Thirrsty is now on your home screen</li>
            </ol>
          </div>
        )}

        {/* Store badges (Coming Soon until URLs added) */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <StoreBadges size="md" variant="row" showLabel testIdPrefix="download-dialog-store-badges" />
        </div>

        {/* QR code for cross-device scan */}
        <div
          className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4"
          data-testid="download-app-qr-block"
        >
          <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg bg-white p-2">
            <QRCodeSVG
              value={SITE_URL}
              size={80}
              level="M"
              bgColor="#ffffff"
              fgColor="#07070A"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-sm font-semibold text-white">
              <Smartphone className="h-4 w-4 text-[#F6B24A]" />
              On desktop? Scan with your phone
            </p>
            <p className="mt-1 text-xs text-white/60 break-all">{SITE_URL}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={copyLink}
              data-testid="download-app-copy-link-button"
              className="mt-2 h-8 px-2 text-[#F6B24A] hover:bg-[#F6B24A]/10"
            >
              {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        </div>

        <p className="text-center text-[11px] text-white/40">
          Free profile · Personal QR · Instant discounts at partner venues
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default DownloadAppDialog;
